import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';
import AuthPage from '@/pages/AuthPage';
import Dashboard from '@/pages/Dashboard';
import Spending from '@/pages/Spending';
import WealthOverview from '@/pages/WealthOverview';
import WealthAccounts from '@/pages/WealthAccounts';
import WealthAllocation from '@/pages/WealthAllocation';
import WealthGoals from '@/pages/WealthGoals';
import WealthHousehold from '@/pages/WealthHousehold';
import WealthDocuments from '@/pages/WealthDocuments';
import Transactions from '@/pages/Transactions';
import Recurring from '@/pages/Recurring';
import Analytics from '@/pages/Analytics';
import Reports from '@/pages/Reports';
import Budgets from '@/pages/Budgets';
import Settings from '@/pages/Settings';
import AuditLogs from '@/pages/AuditLogs';
import Organization from '@/pages/Organization';
import UserManagement from '@/pages/UserManagement';
import APIKeys from '@/pages/APIKeys';
import Webhooks from '@/pages/Webhooks';
import Billing from '@/pages/Billing';
import PlatformBankSettings from '@/pages/PlatformBankSettings';
import ForgotPassword from '@/pages/ForgotPassword';
import SetPassword from '@/pages/SetPassword';
import Sidebar from '@/components/Sidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // SECURITY: tokens are stored in localStorage so the SPA can attach them to
  // axios requests. This pattern is XSS-vulnerable but acceptable for the
  // current threat model because (a) we ship no inline scripts, (b) we don't
  // execute user-provided HTML, and (c) the API also re-validates every JWT
  // server-side. A future hardening pass should migrate to httpOnly cookies
  // with a CSRF token — tracked as P3 in CHANGELOG.
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, exchange session_id for JWT
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const handleOAuthCallback = async () => {
      if (window.location.hash?.includes('session_id=')) {
        const sessionId = window.location.hash.split('session_id=')[1].split('&')[0];
        try {
          const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
          login(res.data.access_token, res.data.user);
          // Strip the hash from URL
          window.history.replaceState(null, '', window.location.pathname);
        } catch (e) {
          console.error('Google sign-in failed', e);
        } finally {
          setLoading(false);
        }
        return true;
      }
      return false;
    };

    const init = async () => {
      const wasOAuth = await handleOAuthCallback();
      if (wasOAuth) return;
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchUser();
      } else {
        setLoading(false);
      }
    };
    init();
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setToken(token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="loading-screen">
        <div className="text-slate-900 font-chivo text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, token }}>
      <BrowserRouter>
        <Toaster position="top-right" />
        {!user ? (
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/forgot" element={<ForgotPassword />} />
            <Route path="/auth/setup" element={<SetPassword kind="setup" />} />
            <Route path="/auth/reset" element={<SetPassword kind="reset" />} />
            <Route path="*" element={<Navigate to="/auth" replace />} />
          </Routes>
        ) : (
          <div className="flex min-h-screen bg-white">
            <Sidebar />
            <div className="flex-1 ml-64">
              <Routes>
                <Route path="/" element={<WealthOverview />} />
                <Route path="/wealth/accounts" element={<WealthAccounts />} />
                <Route path="/wealth/allocation" element={<WealthAllocation />} />
                <Route path="/wealth/goals" element={<WealthGoals />} />
                <Route path="/wealth/documents" element={<WealthDocuments />} />
                <Route path="/wealth/household" element={<WealthHousehold />} />
                <Route path="/spending" element={<Spending />} />
                <Route path="/dashboard-old" element={<Dashboard />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/recurring" element={<Recurring />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/budgets" element={<Budgets />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/organization" element={<Organization />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/admin/platform-bank" element={<PlatformBankSettings />} />
                {user.role === 'admin' && (
                  <>
                    <Route path="/users" element={<UserManagement />} />
                    <Route path="/audit-logs" element={<AuditLogs />} />
                    <Route path="/api-keys" element={<APIKeys />} />
                    <Route path="/webhooks" element={<Webhooks />} />
                  </>
                )}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </div>
        )}
      </BrowserRouter>
    </AuthContext.Provider>
  );
};

export default App;
export { API };